import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import { cn } from '@/lib/utils';
import { useCustomerProperties, useCustomerSearch } from '@/routes/customers/useCustomers';
import { useCreateEstimate } from '@/routes/estimates/useEstimates';

/**
 * The office's "New estimate" from the estimates list (M2-06): which
 * customer, and which of their properties — tax resolves from the property,
 * and M2-05 requires one. An estimate started from a JOB never comes through
 * here; the job page already knows both.
 */
export function NewEstimateDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const id = useId();
  const [term, setTerm] = useState('');
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [propertyId, setPropertyId] = useState('');

  const customers = useCustomerSearch(term, term.trim().length > 0);
  const properties = useCustomerProperties(customerId ?? undefined);
  const active = (properties.data ?? []).filter((p) => p.status === 'Active');
  const create = useCreateEstimate();

  const chosenProperty = propertyId || (active.length === 1 ? active[0]!.id : '');

  return (
    <Dialog
      titleId={`${id}-title`}
      title={t('estimates.newDialog.title')}
      description={t('estimates.newDialog.description')}
      onClose={onClose}
      testId="new-estimate-dialog"
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('estimates.newDialog.cancel')}
          </Button>
          <Button
            type="button"
            disabled={customerId === null || chosenProperty === '' || create.isPending}
            onClick={() =>
              create.mutate(
                { customerId: customerId!, propertyId: chosenProperty, jobId: null },
                { onSuccess: (estimate) => navigate(`/estimates/${estimate.id}`) },
              )
            }
          >
            {create.isPending ? t('estimates.newDialog.creating') : t('estimates.newDialog.create')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label htmlFor={`${id}-customer`} className="mb-1 block text-sm font-medium">
            {t('estimates.newDialog.customer')}
          </label>
          <input
            id={`${id}-customer`}
            className={FIELD_CLASS}
            value={term}
            placeholder={t('estimates.newDialog.customerSearch')}
            autoComplete="off"
            onChange={(event) => {
              setTerm(event.target.value);
              setCustomerId(null);
              setPropertyId('');
            }}
          />
          {term.trim() && customerId === null ? (
            customers.data && customers.data.length === 0 ? (
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                {t('estimates.newDialog.noCustomers')}
              </p>
            ) : (
              <ul className="mt-1 max-h-48 overflow-auto rounded-md border border-[var(--color-border)]">
                {(customers.data ?? []).slice(0, 8).map((customer) => (
                  <li key={customer.id}>
                    <button
                      type="button"
                      className={cn(
                        'block w-full px-3 py-2 text-left text-sm hover:bg-[var(--color-muted)]',
                      )}
                      onClick={() => {
                        setCustomerId(customer.id);
                        setTerm(customer.name);
                      }}
                    >
                      {customer.name}
                    </button>
                  </li>
                ))}
              </ul>
            )
          ) : null}
        </div>

        {customerId !== null && properties.isSuccess ? (
          active.length === 0 ? (
            <p className="text-sm text-[var(--color-muted-foreground)]">
              {t('estimates.newDialog.noProperties')}
            </p>
          ) : (
            <label className="block text-sm">
              <span className="mb-1 block font-medium">{t('estimates.newDialog.property')}</span>
              <select
                className={FIELD_CLASS}
                value={chosenProperty}
                onChange={(event) => setPropertyId(event.target.value)}
              >
                {active.length > 1 ? (
                  <option value="">{t('estimates.newDialog.chooseProperty')}</option>
                ) : null}
                {active.map((property) => (
                  <option key={property.id} value={property.id}>
                    {[property.address.line1, property.unit, property.address.city]
                      .filter(Boolean)
                      .join(', ')}
                  </option>
                ))}
              </select>
            </label>
          )
        ) : null}

        {create.isError ? (
          <p role="alert" className="text-sm text-[var(--color-destructive)]">
            {t('estimates.newDialog.failed')}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
